import React from 'react';
import PropTypes from 'prop-types';
import { Select, Spin } from 'antd';
import './style.scss';

const MAX_MENTORS = 5;

// Inline SVG rather than antd's <Icon type="..." />: the v3 Icon API is removed in v4
// and @ant-design/icons is pinned here at the v3-era ^2.1.1, so an <Icon> would be new
// v3-only surface this component is explicitly meant to avoid.
const ArrowUp = () => (
  <svg viewBox="0 0 10 10" width="10" height="10" aria-hidden="true" focusable="false">
    <path d="M5 1 L9 7 H1 Z" fill="currentColor" />
  </svg>
);

const ArrowDown = () => (
  <svg viewBox="0 0 10 10" width="10" height="10" aria-hidden="true" focusable="false">
    <path d="M1 3 H9 L5 9 Z" fill="currentColor" />
  </svg>
);

const Cross = () => (
  <svg viewBox="0 0 10 10" width="10" height="10" aria-hidden="true" focusable="false">
    <path d="M1 1 L9 9 M9 1 L1 9" stroke="currentColor" strokeWidth="1.5" fill="none" />
  </svg>
);

const labelFor = (options, id) => {
  const found = options.find(o => o.value === id);
  return found ? found.label : String(id);
};

// The single place options become antd Options, so an antd upgrade touches one function.
const renderOptions = options => options.map(o => (
  <Select.Option key={o.value} value={o.value}>{o.label}</Select.Option>
));

// `value` defaults in the signature rather than in defaultProps on purpose: React resolves
// defaultProps into element.props, and antd 3's getFieldDecorator warns when `value` is
// already present on the child it is about to control.
const MentorRankList = ({
  value = [], onChange, disabled, options, onSearch, fetching, currentUserId
}) => {
  // Position is the rank, so ranks are always 1..n with no gaps and cannot desync.
  const move = (from, to) => {
    // Bounds guard local to the handler: the arrows are `disabled` at the ends, but with
    // a negative `to` splice would insert from the END of the array and silently reorder
    // the wrong way (move(0, -1) on [1,2,3] yields [2,1,3]). Keeping the guard here means
    // swapping <button> for a styled element cannot reintroduce that.
    if (to < 0 || to >= value.length) return;
    const next = [...value];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
  };

  const remove = id => onChange(value.filter(v => v !== id));

  // Adding appends to the bottom (least preferred); the user promotes it afterwards.
  const add = (id) => {
    if (value.includes(id) || value.length >= MAX_MENTORS) return;
    onChange([...value, id]);
  };

  const atCap = value.length >= MAX_MENTORS;
  // Exclude already-ranked ids AND the edited user (nobody is their own mentor).
  const searchOptions = options.filter(o => !value.includes(o.value) && o.value !== currentUserId);

  // Pre-fetch a first page of users when the dropdown opens with nothing to show, so the
  // user does not stare at an empty list until they type. `onDropdownVisibleChange` is an
  // antd-3-only prop, so it belongs here with the rest of the antd surface, not in the form.
  // Guarded on searchOptions rather than `options`: `options` is never empty once a saved
  // mentor has been seeded, so that guard would skip the fetch for exactly those users.
  const handleDropdownVisibleChange = (open) => {
    if (open && searchOptions.length === 0) onSearch('');
  };

  return (
    <div className="mentor-rank-list">
      {value.length > 0 && (
        <ul className="mentor-rank-list__rows">
          {value.map((id, i) => {
            const label = labelFor(options, id);
            const primaryBadge = i === 0 && !disabled;
            return (
              <li key={id}>
                <span className={`mentor-rank-list__badge${primaryBadge ? ' mentor-rank-list__badge--primary' : ''}`}>{i + 1}</span>
                <span className="mentor-rank-list__email">{label}</span>
                {!disabled && (
                  <span className="mentor-rank-list__controls">
                    <button type="button" aria-label={`Move ${label} up`} disabled={i === 0} onClick={() => move(i, i - 1)}>
                      <ArrowUp />
                    </button>
                    <button type="button" aria-label={`Move ${label} down`} disabled={i === value.length - 1} onClick={() => move(i, i + 1)}>
                      <ArrowDown />
                    </button>
                    <button type="button" aria-label={`Remove ${label}`} onClick={() => remove(id)}>
                      <Cross />
                    </button>
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {!disabled && (
        <Select
          className="mentor-rank-list__search"
          showSearch
          value={undefined}
          filterOption={false}
          placeholder={atCap ? 'Maximum of 5 mentors' : 'Search by email'}
          notFoundContent={fetching ? <Spin size="small" /> : null}
          onSearch={onSearch}
          onDropdownVisibleChange={handleDropdownVisibleChange}
          onChange={add}
          disabled={atCap}
        >
          {renderOptions(searchOptions)}
        </Select>
      )}
    </div>
  );
};

MentorRankList.propTypes = {
  // Defaulted in the signature rather than defaultProps (see the note on the component),
  // so react/require-default-props has nothing to match and is disabled here.
  // eslint-disable-next-line react/require-default-props
  value: PropTypes.arrayOf(PropTypes.number),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.number,
    label: PropTypes.string
  })),
  onSearch: PropTypes.func.isRequired,
  fetching: PropTypes.bool,
  currentUserId: PropTypes.number
};

MentorRankList.defaultProps = {
  disabled: false,
  options: [],
  fetching: false,
  currentUserId: null
};

export default MentorRankList;
